import { Component } from './component.js';

/**
 * @typedef {Object} SubscriptionOptionsRefs
 * @property {HTMLInputElement} sellingPlanInput
 */

/**
 * @extends {Component<SubscriptionOptionsRefs>}
 */
class SubscriptionOptions extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.cards = [];
    this.radios = [];
    this.productFormVariantInput = null;
    this.originalVariantId = null;
    this.#initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#removeEventListeners();
  }

  #initialize() {
    this.cards = Array.from(this.querySelectorAll('.subscription-options__card'));
    this.radios = Array.from(this.querySelectorAll('.subscription-options__radio'));
    this.#findProductFormInputs();
    this.#addEventListeners();
    this.#moveInputToForm();
    this.#setInitialSelection();
  }

  #setInitialSelection() {
    // Find the card that was pre-selected by Liquid (has --selected class)
    const selectedCard = this.cards.find(card =>
      card.classList.contains('subscription-options__card--selected')
    );

    if (selectedCard) {
      console.log('[SubConfig] Setting up initial selection for:', selectedCard.dataset.optionKey);
      // Trigger selection to set up variant and selling plan inputs correctly
      this.#selectOption(selectedCard);
    } else {
      console.log('[SubConfig] No default option selected');
    }
  }

  #findProductFormInputs() {
    const productForm = this.closest('product-form-component')?.querySelector('form')
      || this.closest('.oct-quiz-products__item')?.querySelector('form[data-type="add-to-cart-form"]')
      || this.closest('.shopify-section')?.querySelector('form[data-type="add-to-cart-form"]')
      || this.closest('.product-details')?.querySelector('form[data-type="add-to-cart-form"]')
      || document.querySelector('form[data-type="add-to-cart-form"]');

    if (productForm) {
      this.productFormVariantInput = productForm.querySelector('input[name="id"]');
      // Store original variant ID for reverting back when selecting one-time
      if (this.productFormVariantInput && !this.originalVariantId) {
        this.originalVariantId = this.productFormVariantInput.value;
        console.log('[SubConfig] Stored original variant ID:', this.originalVariantId);
      }
    }
  }

  #moveInputToForm() {
    if (!this.refs.sellingPlanInput) return;

    const productForm = this.closest('product-form-component')?.querySelector('form')
      || this.closest('.oct-quiz-products__item')?.querySelector('form[data-type="add-to-cart-form"]')
      || this.closest('.shopify-section')?.querySelector('form[data-type="add-to-cart-form"]')
      || this.closest('.product-details')?.querySelector('form[data-type="add-to-cart-form"]')
      || document.querySelector('form[data-type="add-to-cart-form"]');

      console.log(productForm, this.closest('.oct-quiz-products__item')?.querySelector('form[data-type="add-to-cart-form"]'), "product form")

    if (productForm && !productForm.contains(this.refs.sellingPlanInput)) {
      const existingInput = productForm.querySelector('input[name="selling_plan"]');
      if (existingInput) {
        existingInput.value = this.refs.sellingPlanInput.value;
        this.refs.sellingPlanInput = existingInput;
      } else {
        const newInput = document.createElement('input');
        newInput.type = 'hidden';
        newInput.name = 'selling_plan';
        newInput.value = this.refs.sellingPlanInput.value;
        productForm.appendChild(newInput);
        this.refs.sellingPlanInput.remove();
        this.refs.sellingPlanInput = newInput;
      }
    }
  }

  #addEventListeners() {
    for (const card of this.cards) {
      card.addEventListener('click', this.#handleCardClick);
    }

    for (const radio of this.radios) {
      radio.addEventListener('change', this.#handleRadioChange);
    }

    const section = this.closest('.shopify-section, product-form-component');
    if (section) {
      section.addEventListener('variant:update', this.#handleVariantUpdate);
    }
  }

  #removeEventListeners() {
    for (const card of this.cards) {
      card.removeEventListener('click', this.#handleCardClick);
    }

    for (const radio of this.radios) {
      radio.removeEventListener('change', this.#handleRadioChange);
    }

    const section = this.closest('.shopify-section, product-form-component');
    if (section) {
      section.removeEventListener('variant:update', this.#handleVariantUpdate);
    }
  }

  #handleCardClick = (event) => {
    const card = event.currentTarget;
    const radio = card.querySelector('.subscription-options__radio');

    if (radio && event.target !== radio) {
      radio.checked = true;
    }

    this.#selectOption(card);
  };

  #handleRadioChange = (event) => {
    const radio = event.target;
    const card = radio.closest('.subscription-options__card');

    if (card) {
      this.#selectOption(card);
    }
  };

  #handleVariantUpdate = () => {
    this.#findProductFormInputs();
  };

  #selectOption(card) {
    const optionKey = card.dataset.optionKey;
    const sellingPlanId = card.dataset.sellingPlanId;
    const variantId = card.dataset.variantId;
    const showBenefits = card.dataset.showBenefits === 'true';
    const isOneTime = card.dataset.isOneTime === 'true';

    console.log('[SubConfig] Selecting option:', {
      optionKey,
      sellingPlanId,
      variantId,
      isOneTime
    });

    // Update card selected state
    for (const c of this.cards) {
      c.classList.remove('subscription-options__card--selected');
    }
    card.classList.add('subscription-options__card--selected');

    // Update radio button checked state
    const radio = card.querySelector('.subscription-options__radio');
    if (radio) {
      radio.checked = true;
    }

    this.#updateBenefitsVisibility(card, showBenefits && !isOneTime);
    this.#updateSellingPlanInput(isOneTime ? '' : sellingPlanId);

    // Handle variant switching
    if (variantId && variantId !== 'null' && this.productFormVariantInput) {
      // Switch to the specified variant (e.g., prepaid bundles)
      console.log('[SubConfig] Switching variant from', this.productFormVariantInput.value, 'to', variantId);
      this.productFormVariantInput.value = variantId;
    } else if (this.originalVariantId && this.productFormVariantInput) {
      // No variant specified: revert to original variant (for monthly subscription or one-time)
      console.log('[SubConfig] Reverting to original variant:', this.originalVariantId);
      this.productFormVariantInput.value = this.originalVariantId;
    } else {
      console.log('[SubConfig] Not switching variant:', { variantId, hasOriginal: !!this.originalVariantId, hasFormInput: !!this.productFormVariantInput });
    }

    this.dispatchEvent(new CustomEvent('subscription:change', {
      bubbles: true,
      detail: {
        optionKey,
        sellingPlanId: isOneTime ? null : sellingPlanId,
        variantId,
        isOneTime
      }
    }));
  }

  #updateBenefitsVisibility(selectedCard, showBenefits) {
    for (const card of this.cards) {
      const benefits = card.querySelector('.subscription-options__benefits');
      if (benefits) {
        const isSelected = card === selectedCard;
        const shouldShow = isSelected && showBenefits;

        if (shouldShow) {
          benefits.removeAttribute('hidden');
        } else {
          benefits.setAttribute('hidden', '');
        }
      }
    }
  }

  #updateSellingPlanInput(sellingPlanId) {
    if (this.refs.sellingPlanInput) {
      this.refs.sellingPlanInput.value = sellingPlanId || '';
      console.log('[SubConfig] Updated selling_plan input:', {
        value: this.refs.sellingPlanInput.value,
        name: this.refs.sellingPlanInput.name
      });
    } else {
      console.log('[SubConfig] No selling_plan input found!');
    }
  }

  getSelectedOption() {
    const selectedCard = this.querySelector('.subscription-options__card--selected');
    if (!selectedCard) return null;

    return {
      optionKey: selectedCard.dataset.optionKey,
      sellingPlanId: selectedCard.dataset.sellingPlanId,
      variantId: selectedCard.dataset.variantId,
      isOneTime: selectedCard.dataset.isOneTime === 'true'
    };
  }

  selectOptionByKey(optionKey) {
    const card = this.cards.find(c => c.dataset.optionKey === optionKey);
    if (card) {
      const radio = card.querySelector('.subscription-options__radio');
      if (radio) radio.checked = true;
      this.#selectOption(card);
    }
  }
}

if (!customElements.get('subscription-options')) {
  customElements.define('subscription-options', SubscriptionOptions);
}
